"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthStore = void 0;
const auth_1 = require("@App/api/routes/auth");
const jwt_1 = require("@App/enum/jwt");
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const date_helpers_1 = require("@App/helpers/date-helpers");
const mobx_1 = require("mobx");
const api_client_1 = require("@App/api/api-client");
class AuthStore {
    constructor(rootStore) {
        Object.defineProperty(this, "isLogged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "interceptorRef", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "loadDataOnLogin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                yield this.rootStore.userStore.loadUserInfo();
                yield Promise.all([this.rootStore.userStore.loadUserSettings(), this.loadRightsholdersOnLogin()]);
                this.rootStore.notificationStore.getNotificationToken();
            })
        });
        Object.defineProperty(this, "loadRightsholdersOnLogin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                if (!this.rootStore.userStore.isInternal) {
                    yield Promise.all([
                        this.rootStore.rightsholderSearchStore.loadRightsholders({}),
                        this.rootStore.songwriterSearchStore.loadSongwriters({}),
                    ]);
                }
            })
        });
        Object.defineProperty(this, "persistToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (jwtToken) => {
                this.setJwtTokenToHeader(jwtToken);
                localStorage.setItem(jwt_1.JWT_STORAGE_NAMES.accessToken, jwtToken);
            }
        });
        Object.defineProperty(this, "setJwtTokenToHeader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (jwtToken) => {
                // There should never be more than one JWT, but let's be extra careful to track them all
                const newRef = api_client_1.axiosClient.interceptors.request.use((request) => {
                    request.headers.set('Authorization', `Bearer ${jwtToken}`);
                    return request;
                });
                this.interceptorRef.push(newRef);
            }
        });
        Object.defineProperty(this, "removeJwtTokenHeader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                // There should never be more than one JWT, but let's be extra careful to remove them all
                const idsToRemove = this.interceptorRef.slice();
                this.interceptorRef = [];
                idsToRemove.forEach((id) => api_client_1.axiosClient.interceptors.request.eject(id));
            }
        });
        (0, mobx_1.makeObservable)(this, {
            isLogged: mobx_1.observable,
            setLoggedIn: mobx_1.action,
            logOut: mobx_1.action,
            clearLoginData: mobx_1.action,
        });
        this.rootStore = rootStore;
    }
    get isLoggedIn() {
        return this.isLogged;
    }
    setLoggedIn(loggedIn) {
        this.isLogged = loggedIn;
    }
    logOut() {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.clear();
            yield (0, auth_1.logOut)();
            this.clearLoginData();
        });
    }
    clearLoginData() {
        this.removeJwtTokenHeader();
        this.interceptorRef = [];
        this.setLoggedIn(false);
        localStorage.clear();
        this.rootStore.resetStore();
    }
    login(username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const body = { username, password };
                (0, auth_1.login)(body)
                    .then((response) => __awaiter(this, void 0, void 0, function* () {
                    const tokenResponse = response.data;
                    if (tokenResponse.message && tokenResponse.message === 'mfa_required') {
                        localStorage.setItem(jwt_1.JWT_STORAGE_NAMES.mfaToken, tokenResponse.applicable);
                        resolve(tokenResponse);
                        return;
                    }
                    else if (!!tokenResponse.applicable && tokenResponse.message === '') {
                        // For users with MFA turned off (MPOR-1588)
                        this.persistToken(tokenResponse.applicable);
                        this.setLoggedIn(true);
                        yield this.loadDataOnLogin();
                        resolve(tokenResponse);
                    }
                    else if (tokenResponse.message === 'enroll_mfa') {
                        resolve(tokenResponse);
                    }
                }))
                    .catch((error) => {
                    this.setLoggedIn(false);
                    this.rootStore.popupStore.setAlertData(error.message, true);
                    reject(error.message);
                });
            });
        });
    }
    mfaSubmit(code) {
        return __awaiter(this, void 0, void 0, function* () {
            const mfaToken = localStorage.getItem(jwt_1.JWT_STORAGE_NAMES.mfaToken);
            if (!mfaToken) {
                throw new Error('Could not find MFA token');
            }
            const body = { code, mfaToken };
            let tokenResponse;
            try {
                const response = yield (0, auth_1.mfaSubmit)(body);
                tokenResponse = response.data;
            }
            catch (ignore) {
                this.rootStore.popupStore.setAlertData('MFA code entered is expired or invalid. Please contact Support for a new code.', true);
                throw new Error('MFA code entered is expired or invalid. Please contact Support for a new code.');
            }
            if (!tokenResponse.error) {
                this.persistToken(tokenResponse.applicable);
                localStorage.removeItem(jwt_1.JWT_STORAGE_NAMES.mfaToken);
            }
            else {
                this.rootStore.popupStore.setAlertData('Unable To Confirm MFA', true);
                throw new Error('Unable To Confirm MFA');
            }
            yield this.loadDataOnLogin();
            this.setLoggedIn(true);
        });
    }
    forgotPassword(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, auth_1.forgotPassword)(username).catch((error) => this.rootStore.popupStore.setAlertData(error.message, true));
        });
    }
    confirmForgotPassword(username, confirmationCode, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, auth_1.confirmForgotPassword)(username, confirmationCode, newPassword);
        });
    }
    checkUserAuthorized() {
        const token = localStorage.getItem(jwt_1.JWT_STORAGE_NAMES.accessToken);
        if (token) {
            if (AuthStore.checkForExpired(token)) {
                this.setJwtTokenToHeader(token);
                this.setLoggedIn(!!token);
                if (token) {
                    return this.loadDataOnLogin();
                }
            }
            else {
                this.clearLoginData();
                return Promise.resolve();
            }
        }
        else {
            this.setLoggedIn(false);
        }
        return Promise.resolve();
    }
    static checkForExpired(token) {
        const currentDateInTimestamp = (0, date_helpers_1.getCurrentDateInTimestamp)();
        const decodeToken = (0, jwt_decode_1.default)(token || '');
        return decodeToken.exp !== undefined && decodeToken.exp > currentDateInTimestamp;
    }
}
exports.AuthStore = AuthStore;
